import { environment } from 'src/environments/environment'
import { Component, EventEmitter, Input, Output } from '@angular/core'
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { Store } from '@ngrx/store'
import { Observable } from 'rxjs'
import { AppState } from 'src/app/core/core.state'
import { configurationOptionSelectors } from '../../configuration-option.selectors'
import { productSelectors } from '../../../product/product.selectors'
import { map } from 'rxjs/operators'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { productActions } from '../../../product/product.actions'
import { ProductGroupsPurchaseFlow } from '../../../../shared/models'
import { markFormGroupDirty } from '../../../../shared/utils/mark-form-dirty'
import { AccessRights } from 'src/app/features/role/user-role/user-role'

@Component({
  selector: 'configuration-option-product-groups-purchase-flow',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class ConfigurationOptionProductGroupsPurchaseFlowCardComponent {
  @Output() onSubmit = new EventEmitter<any>()
  @Input() formValues: any
  public isCollapsed = false
  public cfIsSelected: boolean = false

  public form: UntypedFormGroup = this.fb.group({})
  public isLoading$: Observable<boolean>

  public products$ = this.store.select(productSelectors.selectAll)
  public accessRights = AccessRights

  public allowAsLinkedProducts$ = this.products$.pipe(
    map((products) =>
      products.filter((product) => product.allowAsLinkedProduct)
    )
  )
  public availableLanguages = environment.frontendLanguages.reduce(
    (acc, curr) => {
      if (curr != 'xx') return (acc[curr] = true), acc
      else return acc
    },
    {}
  )

  constructor(private store: Store<AppState>, private fb: UntypedFormBuilder) {}

  get dateGroupsForm() {
    return this.form?.controls?.datesGroups as UntypedFormGroup
  }

  get roomsForm() {
    return this.form?.controls?.rooms as UntypedFormGroup
  }

  get roomsCategoriesForm() {
    return this.roomsForm?.controls?.categoriesProductIds as UntypedFormGroup
  }

  get facilitiesForm() {
    return this.form?.controls?.facilities as UntypedFormGroup
  }

  get facilitiesCategoriesForm() {
    return this.facilitiesForm?.controls
      ?.categoriesProductIds as UntypedFormGroup
  }

  get cateringForm() {
    return this.form?.controls?.catering as UntypedFormGroup
  }
  get noteMandatoryForm() {
    return this.form?.controls?.noteMandatory as UntypedFormGroup
  }
  get cateringCategoriesForm() {
    return this.cateringForm?.controls?.categoriesProductIds as UntypedFormGroup
  }

  public ngOnInit() {
    this.form = this.fb.group({
      datesGroups: this.fb.group({
        ordinal: [null, Validators.required],
        title_en: [null, Validators.required],
        title_nl: [null, Validators.required],
        title_fr: [
          null,
          this.availableLanguages['fr'] ? Validators.required : []
        ],
        title_de: [
          null,
          this.availableLanguages['de'] ? Validators.required : []
        ]
      }),
      rooms: this.fb.group({
        ordinal: [null, Validators.required],
        title_en: [null, Validators.required],
        title_nl: [null, Validators.required],
        title_fr: [
          null,
          this.availableLanguages['fr'] ? Validators.required : []
        ],
        title_de: [
          null,
          this.availableLanguages['de'] ? Validators.required : []
        ],
        productIds: [null, Validators.required],
        categoriesProductIds: this.fb.group({
          kids: [null],
          adults: [null],
          profs: [null],
          commercials: [null]
        })
      }),
      facilities: this.fb.group({
        ordinal: [null, Validators.required],
        title_en: [null, Validators.required],
        title_nl: [null, Validators.required],
        title_fr: [
          null,
          this.availableLanguages['fr'] ? Validators.required : []
        ],
        title_de: [
          null,
          this.availableLanguages['de'] ? Validators.required : []
        ],
        productIds: [null, Validators.required],
        categoriesProductIds: this.fb.group({
          kids: [[]],
          adults: [[]],
          profs: [[]],
          commercials: [[]],
          externalMultiDay: [[]],
          externalSingleDay: [[]],
          internalMultiDay: [[]],
          internalSingleDay: [[]]
        })
      }),
      catering: this.fb.group({
        ordinal: [null, Validators.required],
        title_en: [null, Validators.required],
        title_nl: [null, Validators.required],
        title_fr: [
          null,
          this.availableLanguages['fr'] ? Validators.required : []
        ],
        title_de: [
          null,
          this.availableLanguages['de'] ? Validators.required : []
        ],
        productIds: [null, Validators.required],
        categoriesProductIds: this.fb.group({
          kids: [[]],
          adults: [[]],
          profs: [[]],
          commercials: [[]]
        })
      }),
      noteMandatory: this.fb.group({
        isMandatory: [false, Validators.required]
      })
    })

    this.isLoading$ = this.store.select(
      configurationOptionSelectors.selectIsLoading
    )
    this.setFormValues()
  }

  public onFormSubmit(): void {
    if (this.form.invalid) {
      markFormGroupDirty(this.form)
      return
    }
    if (this.cfIsSelected) {
      this.onSubmit.emit({
        product_groups_purchase_flow_cf: JSON.stringify(this.form.value)
      })
    } else {
      this.onSubmit.emit({
        product_groups_purchase_flow: JSON.stringify(this.form.value)
      })
    }
  }

  public patchValueForm(field, value: boolean) {
    let patchValue = {}

    patchValue[field] = value
    this.noteMandatoryForm.patchValue(patchValue)
  }
  public setFormValues() {
    this.cfIsSelected = !this.cfIsSelected
    if (this.formValues?.product_groups_purchase_flow && !this.cfIsSelected) {
      const configSets: ProductGroupsPurchaseFlow = JSON.parse(
        this.formValues?.product_groups_purchase_flow
      )

      this.store.dispatch(
        productActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: { id: { [CondOperator.IN]: this.getProuctIds(configSets) } }
          })
        })
      )

      this.form?.patchValue(configSets)
    } else if (
      this.formValues?.product_groups_purchase_flow_cf &&
      this.cfIsSelected
    ) {
      const configSets: ProductGroupsPurchaseFlow = JSON.parse(
        this.formValues?.product_groups_purchase_flow_cf
      )
      this.store.dispatch(
        productActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: { id: { [CondOperator.IN]: this.getProuctIds(configSets) } }
          })
        })
      )
      this.form?.patchValue(configSets)
    }
  }
  private getProuctIds(array) {
    let productsIds = []
    Object.values(array).map((item: any) => {
      if (item.productIds) {
        productsIds = [...productsIds, ...item.productIds]
      }
    })
    return [...new Set([...productsIds])]
  }
}
